<template>
  <div class="h-100">
    <Layout ref="layout">
      <template v-slot:body>
        <div class="container-fluid p-4">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 class="mb-0">Gerenciar Leituras</h4>
            </div>
            <button class="btn btn-secondary" @click="voltar">
              <i class="bx bx-arrow-back"></i> Voltar
            </button>
          </div>

          <!-- Informações do Dispositivo -->
          <div class="card mb-4" v-if="dispositivo">
            <div class="card-body py-3">
              <div class="row">
                <div class="col-12 col-md-6 col-lg-3 mb-2 mb-lg-0">
                  <small class="text-muted d-block">Dispositivo</small>
                  <strong>{{ dispositivo.nome || '-' }}</strong>
                  <span class="text-muted ms-1">({{ dispositivo.qrcode }})</span>
                </div>
                <div class="col-12 col-md-6 col-lg-3 mb-2 mb-lg-0">
                  <small class="text-muted d-block">Proprietário</small>
                  <strong>{{ dispositivo.proprietario || '-' }}</strong>
                </div>
                <div class="col-12 col-md-6 col-lg-3 mb-2 mb-lg-0">
                  <small class="text-muted d-block">Localização</small>
                  <strong>{{ formatLocalizacao() }}</strong>
                </div>
                <div class="col-12 col-md-6 col-lg-3">
                  <small class="text-muted d-block">Modelo / Firmware</small>
                  <strong>{{ dispositivo.modelo || '-' }}</strong>
                  <span class="text-muted ms-1" v-if="dispositivo.versaoFirmware">v{{ dispositivo.versaoFirmware }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Filtros -->
          <div class="card mb-4">
            <div class="card-body">
              <div class="row g-3 align-items-end">
                <!-- Data/Hora Início -->
                <div class="col-12 col-md-6 col-lg-3">
                  <label class="form-label">Data/Hora Início</label>
                  <input
                    type="text"
                    class="form-control"
                    ref="datePickerInicio"
                    placeholder="Selecione data/hora"
                  />
                </div>
                <!-- Data/Hora Fim -->
                <div class="col-12 col-md-6 col-lg-3">
                  <label class="form-label">Data/Hora Fim</label>
                  <input
                    type="text"
                    class="form-control"
                    ref="datePickerFim"
                    placeholder="Selecione data/hora"
                  />
                </div>
                <!-- Status das Leituras -->
                <div class="col-12 col-md-6 col-lg-3">
                  <label class="form-label">Status</label>
                  <select class="form-select" v-model="filtros.statusFiltro">
                    <option value="ativas">Somente ativas</option>
                    <option value="todas">Ativas e deletadas</option>
                    <option value="deletadas">Somente deletadas</option>
                  </select>
                </div>
                <!-- Botões -->
                <div class="col-12 col-md-6 col-lg-3">
                  <div class="d-flex gap-2 align-items-start">
                    <button class="btn btn-outline-secondary flex-grow-1" @click="limparFiltros">
                      Limpar
                    </button>
                    <button class="btn btn-primary flex-grow-1" @click="aplicarFiltros">
                      Filtrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabela de Leituras -->
          <div class="card">
            <div class="card-body">
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Carregando...</span>
                </div>
              </div>

              <div v-else-if="leituras.length === 0" class="text-center py-5">
                <p class="text-muted">Nenhuma leitura encontrada</p>
              </div>

              <div v-else>
                <!-- Barra de ação em lote — desktop (sticky no topo da lista) -->
                <div v-if="selectedIds.size > 0" class="bulk-action-bar d-none d-md-block mb-3">
                  <div class="bulk-action-inner d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 rounded">
                    <div class="d-flex align-items-center gap-2">
                      <i class="bx bx-check-circle"></i>
                      <strong>{{ selectedIds.size }}</strong>
                      <span>{{ selectedIds.size === 1 ? 'leitura selecionada' : 'leituras selecionadas' }}</span>
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                      <button class="btn btn-outline-secondary btn-sm" @click="limparSelecao">
                        Limpar seleção
                      </button>
                      <button
                        v-if="qtdSelecionadasAtivas > 0"
                        class="btn btn-danger btn-sm"
                        @click="confirmarDeleteBulk"
                      >
                        <i class="bx bx-trash me-1"></i>
                        Deletar {{ qtdSelecionadasAtivas }} {{ qtdSelecionadasAtivas === 1 ? 'selecionada' : 'selecionadas' }}
                      </button>
                      <button
                        v-if="qtdSelecionadasDeletadas > 0"
                        class="btn btn-success btn-sm"
                        @click="confirmarRestoreBulk"
                      >
                        <i class="bx bx-undo me-1"></i>
                        Restaurar {{ qtdSelecionadasDeletadas }} {{ qtdSelecionadasDeletadas === 1 ? 'selecionada' : 'selecionadas' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Desktop: Tabela -->
                <div class="table-responsive d-none d-md-block">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th style="width: 40px;">
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :checked="isTodosSelecionadosPagina"
                            :indeterminate.prop="isSelecaoParcial"
                            @change="toggleSelecionarTodos"
                            title="Selecionar todos da página"
                          />
                        </th>
                        <th>Data/Hora</th>
                        <th>Firmware</th>
                        <th>Origem</th>
                        <th>Sensores</th>
                        <th>Status</th>
                        <th style="width: 150px;">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="leitura in leituras"
                        :key="leitura.id"
                        :class="{ 'table-danger': leitura.isDeleted, 'table-active': selectedIds.has(leitura.id) }"
                      >
                        <td>
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :checked="selectedIds.has(leitura.id)"
                            @change="toggleSelecionado(leitura.id)"
                          />
                        </td>
                        <td>{{ formatDateTime(leitura.dateTime) }}</td>
                        <td>{{ leitura.infoFirmwareVersion || '-' }}</td>
                        <td>{{ leitura.infoOrigem || '-' }}</td>
                        <td>
                          <span class="badge bg-secondary me-1" v-for="tipo in leitura.sensorTypes" :key="tipo">
                            {{ formatSensorType(tipo) }}
                          </span>
                          <span class="text-muted">({{ leitura.sensorCount }})</span>
                        </td>
                        <td>
                          <span v-if="leitura.isDeleted" class="badge bg-danger">
                            Deletado
                          </span>
                          <span v-else class="badge bg-success">
                            Ativo
                          </span>
                        </td>
                        <td>
                          <button
                            class="btn btn-sm btn-outline-primary me-1"
                            @click="verDetalhes(leitura)"
                            title="Ver detalhes"
                          >
                            <i class="bx bx-show"></i>
                          </button>
                          <button
                            v-if="!leitura.isDeleted"
                            class="btn btn-sm btn-outline-danger"
                            @click="confirmarDelete(leitura)"
                            title="Deletar leitura"
                          >
                            <i class="bx bx-trash"></i>
                          </button>
                          <button
                            v-else
                            class="btn btn-sm btn-outline-success"
                            @click="confirmarRestore(leitura)"
                            title="Restaurar leitura"
                          >
                            <i class="bx bx-undo"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Mobile: Cards -->
                <div class="d-md-none" :class="{ 'pb-bulk-toolbar': selectedIds.size > 0 }">
                  <!-- Selecionar todos (mobile) -->
                  <div class="mobile-select-all d-flex align-items-center gap-2 mb-3 px-3 py-2 rounded">
                    <input
                      type="checkbox"
                      class="form-check-input m-0"
                      :checked="isTodosSelecionadosPagina"
                      :indeterminate.prop="isSelecaoParcial"
                      @change="toggleSelecionarTodos"
                      id="mobile-select-all"
                    />
                    <label for="mobile-select-all" class="mb-0 flex-grow-1">
                      <strong v-if="selectedIds.size > 0">{{ selectedIds.size }} selecionada{{ selectedIds.size === 1 ? '' : 's' }}</strong>
                      <span v-else>Selecionar todos da página</span>
                    </label>
                  </div>

                  <div
                    v-for="leitura in leituras"
                    :key="'card-' + leitura.id"
                    class="card mb-3 leitura-card"
                    :class="{ 'border-danger': leitura.isDeleted, 'border-primary': selectedIds.has(leitura.id) }"
                  >
                    <div class="card-body">
                      <!-- Status Badge + Checkbox -->
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div class="d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :checked="selectedIds.has(leitura.id)"
                            @change="toggleSelecionado(leitura.id)"
                          />
                          <span v-if="leitura.isDeleted" class="badge bg-danger">
                            Deletado
                          </span>
                          <span v-else class="badge bg-success">
                            Ativo
                          </span>
                        </div>
                      </div>

                      <!-- Data/Hora -->
                      <div class="mb-2">
                        <small class="text-muted d-block">Data/Hora</small>
                        <strong>{{ formatDateTime(leitura.dateTime) }}</strong>
                      </div>

                      <!-- Firmware / Origem -->
                      <div class="row mb-2">
                        <div class="col-6">
                          <small class="text-muted d-block">Firmware</small>
                          <span>{{ leitura.infoFirmwareVersion || '-' }}</span>
                        </div>
                        <div class="col-6">
                          <small class="text-muted d-block">Origem</small>
                          <span>{{ leitura.infoOrigem || '-' }}</span>
                        </div>
                      </div>

                      <!-- Sensores -->
                      <div class="mb-3">
                        <small class="text-muted d-block mb-1">Sensores ({{ leitura.sensorCount }})</small>
                        <div>
                          <span
                            class="badge bg-secondary me-1 mb-1"
                            v-for="tipo in leitura.sensorTypes"
                            :key="tipo"
                          >
                            {{ formatSensorType(tipo) }}
                          </span>
                        </div>
                      </div>

                      <!-- Ações -->
                      <div class="d-flex gap-2 pt-2 border-top">
                        <button
                          class="btn btn-outline-primary flex-grow-1"
                          @click="verDetalhes(leitura)"
                        >
                          <i class="bx bx-show me-1"></i> Detalhes
                        </button>
                        <button
                          v-if="!leitura.isDeleted"
                          class="btn btn-outline-danger flex-grow-1"
                          @click="confirmarDelete(leitura)"
                        >
                          <i class="bx bx-trash me-1"></i> Deletar
                        </button>
                        <button
                          v-else
                          class="btn btn-outline-success flex-grow-1"
                          @click="confirmarRestore(leitura)"
                        >
                          <i class="bx bx-undo me-1"></i> Restaurar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Paginação -->
                <div class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">
                  <div class="text-muted small">
                    Mostrando {{ leituras.length }} de {{ total }} leituras
                    <span v-if="selectedIds.size > 0">· <strong>{{ selectedIds.size }}</strong> selecionada{{ selectedIds.size === 1 ? '' : 's' }}</span>
                  </div>
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <div class="d-flex align-items-center gap-2">
                      <label class="text-muted small mb-0 d-none d-md-inline">Itens por página:</label>
                      <select class="form-select form-select-sm" style="width: auto;" :value="limit" @change="alterarLimit($event.target.value)">
                        <option v-for="opt in opcoesLimit" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </div>
                    <button
                      class="btn btn-outline-secondary"
                      :disabled="offset === 0"
                      @click="paginaAnterior"
                    >
                      <i class="bx bx-chevron-left d-md-none"></i>
                      <span class="d-none d-md-inline">Anterior</span>
                    </button>
                    <button
                      class="btn btn-outline-secondary"
                      :disabled="offset + limit >= total"
                      @click="proximaPagina"
                    >
                      <span class="d-none d-md-inline">Próximo</span>
                      <i class="bx bx-chevron-right d-md-none"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Layout>

    <!-- Toolbar de ação em lote — mobile (fixa no rodapé) -->
    <div v-if="selectedIds.size > 0" class="bulk-toolbar-mobile d-md-none">
      <div class="d-flex align-items-center gap-2 mb-2">
        <i class="bx bx-check-circle"></i>
        <strong>{{ selectedIds.size }}</strong>
        <span>{{ selectedIds.size === 1 ? 'selecionada' : 'selecionadas' }}</span>
        <button class="btn btn-link btn-sm ms-auto p-0 text-decoration-none" @click="limparSelecao">
          Limpar
        </button>
      </div>
      <div class="d-flex gap-2">
        <button
          v-if="qtdSelecionadasAtivas > 0"
          class="btn btn-danger flex-grow-1"
          @click="confirmarDeleteBulk"
        >
          <i class="bx bx-trash me-1"></i>
          Deletar {{ qtdSelecionadasAtivas }}
        </button>
        <button
          v-if="qtdSelecionadasDeletadas > 0"
          class="btn btn-success flex-grow-1"
          @click="confirmarRestoreBulk"
        >
          <i class="bx bx-undo me-1"></i>
          Restaurar {{ qtdSelecionadasDeletadas }}
        </button>
      </div>
    </div>

    <!-- Modal de Confirmação de Delete -->
    <div class="modal fade" ref="modalDelete" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Exclusão</h5>
            <button type="button" class="btn-close" @click="fecharModalDelete"></button>
          </div>
          <div class="modal-body">
            <p>Tem certeza que deseja deletar esta leitura?</p>
            <p class="text-muted small">
              Esta ação marcará a leitura e todos os seus sensores como deletados.
              Ela poderá ser restaurada posteriormente.
            </p>
            <div class="mb-3">
              <label class="form-label">Motivo (opcional)</label>
              <textarea
                class="form-control"
                v-model="deleteReason"
                rows="2"
                placeholder="Informe o motivo da exclusão..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalDelete">
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" @click="executarDelete" :disabled="deletando">
              <span v-if="deletando" class="spinner-border spinner-border-sm me-1"></span>
              Deletar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Restore -->
    <div class="modal fade" ref="modalRestore" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Restauração</h5>
            <button type="button" class="btn-close" @click="fecharModalRestore"></button>
          </div>
          <div class="modal-body">
            <p>Tem certeza que deseja restaurar esta leitura?</p>
            <p class="text-muted small">
              Esta ação restaurará a leitura e todos os seus sensores.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalRestore">
              Cancelar
            </button>
            <button type="button" class="btn btn-success" @click="executarRestore" :disabled="restaurando">
              <span v-if="restaurando" class="spinner-border spinner-border-sm me-1"></span>
              Restaurar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Delete em Lote -->
    <div class="modal fade" ref="modalDeleteBulk" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Exclusão em Lote</h5>
            <button type="button" class="btn-close" @click="fecharModalDeleteBulk"></button>
          </div>
          <div class="modal-body">
            <p>
              Tem certeza que deseja deletar
              <strong>{{ qtdSelecionadasAtivas }}</strong>
              {{ qtdSelecionadasAtivas === 1 ? 'leitura' : 'leituras' }}?
            </p>
            <p class="text-muted small">
              Esta ação marcará todas as leituras selecionadas e seus sensores como deletados.
              Elas poderão ser restauradas posteriormente.
            </p>
            <div class="mb-3">
              <label class="form-label">Motivo (opcional)</label>
              <textarea
                class="form-control"
                v-model="bulkDeleteReason"
                rows="2"
                placeholder="Informe o motivo da exclusão..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalDeleteBulk">
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" @click="executarDeleteBulk" :disabled="deletandoBulk">
              <span v-if="deletandoBulk" class="spinner-border spinner-border-sm me-1"></span>
              Deletar {{ qtdSelecionadasAtivas }} {{ qtdSelecionadasAtivas === 1 ? 'leitura' : 'leituras' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Restore em Lote -->
    <div class="modal fade" ref="modalRestoreBulk" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Restauração em Lote</h5>
            <button type="button" class="btn-close" @click="fecharModalRestoreBulk"></button>
          </div>
          <div class="modal-body">
            <p>
              Tem certeza que deseja restaurar
              <strong>{{ qtdSelecionadasDeletadas }}</strong>
              {{ qtdSelecionadasDeletadas === 1 ? 'leitura' : 'leituras' }}?
            </p>
            <p class="text-muted small">
              Esta ação restaurará todas as leituras selecionadas e seus sensores.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalRestoreBulk">
              Cancelar
            </button>
            <button type="button" class="btn btn-success" @click="executarRestoreBulk" :disabled="restaurandoBulk">
              <span v-if="restaurandoBulk" class="spinner-border spinner-border-sm me-1"></span>
              Restaurar {{ qtdSelecionadasDeletadas }} {{ qtdSelecionadasDeletadas === 1 ? 'leitura' : 'leituras' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Aviso: Perda de Seleção -->
    <div class="modal fade" ref="modalPerderSelecao" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Você perderá sua seleção</h5>
            <button type="button" class="btn-close" @click="fecharModalPerderSelecao"></button>
          </div>
          <div class="modal-body">
            <p>
              Você tem <strong>{{ selectedIds.size }}</strong>
              {{ selectedIds.size === 1 ? 'leitura selecionada' : 'leituras selecionadas' }}.
            </p>
            <p class="text-muted small">
              Esta ação vai limpar sua seleção atual. Deseja continuar?
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="fecharModalPerderSelecao">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="confirmarPerdaSelecao">
              Continuar e limpar seleção
            </button>
          </div>
        </div>
      </div>
    </div>

    <Alert ref="alert"></Alert>
  </div>
</template>

<script>
import Layout from "../../components/layout/Tema.vue";
import Alert from "../../components/Alert.vue";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

export default {
  components: {
    Layout,
    Alert,
  },
  data() {
    return {
      qrCodeId: null,
      dispositivo: null,
      leituras: [],
      total: 0,
      loading: false,
      limit: 50,
      offset: 0,
      opcoesLimit: [25, 50, 100, 200],
      filtros: {
        dataHoraInicio: null,
        dataHoraFim: null,
        statusFiltro: "ativas", // ativas, todas, deletadas
      },
      leituraSelecionada: null,
      deleteReason: "",
      deletando: false,
      restaurando: false,
      fpInicio: null,
      fpFim: null,
      // Seleção em lote
      selectedIds: new Set(),
      bulkDeleteReason: "",
      deletandoBulk: false,
      restaurandoBulk: false,
      acaoPosPerdaSelecao: null,
    };
  },
  computed: {
    qtdSelecionadasAtivas() {
      return this.leituras.filter(l => this.selectedIds.has(l.id) && !l.isDeleted).length;
    },
    qtdSelecionadasDeletadas() {
      return this.leituras.filter(l => this.selectedIds.has(l.id) && l.isDeleted).length;
    },
    isTodosSelecionadosPagina() {
      return this.leituras.length > 0 && this.leituras.every(l => this.selectedIds.has(l.id));
    },
    isSelecaoParcial() {
      const selecionadosNaPagina = this.leituras.filter(l => this.selectedIds.has(l.id)).length;
      return selecionadosNaPagina > 0 && selecionadosNaPagina < this.leituras.length;
    },
  },
  mounted() {
    this.qrCodeId = this.$route.params.qrCodeId;
    this.restaurarFiltrosDaUrl();
    this.inicializarFlatpickr();
    this.carregarDispositivo();
    this.buscarLeituras();
  },
  beforeUnmount() {
    if (this.fpInicio) this.fpInicio.destroy();
    if (this.fpFim) this.fpFim.destroy();
  },
  methods: {
    restaurarFiltrosDaUrl() {
      const query = this.$route.query;
      if (query.dataInicio) {
        this.filtros.dataHoraInicio = query.dataInicio;
      }
      if (query.dataFim) {
        this.filtros.dataHoraFim = query.dataFim;
      }
      if (query.status && ['ativas', 'todas', 'deletadas'].includes(query.status)) {
        this.filtros.statusFiltro = query.status;
      }
      if (query.offset) {
        this.offset = parseInt(query.offset) || 0;
      }
      if (query.limit) {
        const limitParsed = parseInt(query.limit);
        if (this.opcoesLimit.includes(limitParsed)) {
          this.limit = limitParsed;
        }
      }
    },
    atualizarUrl() {
      const query = {};
      if (this.filtros.dataHoraInicio) {
        query.dataInicio = this.filtros.dataHoraInicio;
      }
      if (this.filtros.dataHoraFim) {
        query.dataFim = this.filtros.dataHoraFim;
      }
      if (this.filtros.statusFiltro && this.filtros.statusFiltro !== 'ativas') {
        query.status = this.filtros.statusFiltro;
      }
      if (this.offset > 0) {
        query.offset = this.offset.toString();
      }
      if (this.limit !== 50) {
        query.limit = this.limit.toString();
      }

      // Atualiza a URL sem recarregar a página
      this.$router.replace({ query }).catch(() => {});
    },
    inicializarFlatpickr() {
      const hoje = new Date();
      const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0);
      const fimHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 0);

      // Usa valores da URL se existirem, senão usa valores padrão (hoje)
      const dataInicioDefault = this.filtros.dataHoraInicio
        ? this.parseISOToDate(this.filtros.dataHoraInicio)
        : inicioHoje;
      const dataFimDefault = this.filtros.dataHoraFim
        ? this.parseISOToDate(this.filtros.dataHoraFim)
        : fimHoje;

      const configBase = {
        locale: Portuguese,
        enableTime: true,
        time_24hr: true,
        dateFormat: "d/m/Y H:i",
        allowInput: true,
      };

      // Data/Hora Início
      this.fpInicio = flatpickr(this.$refs.datePickerInicio, {
        ...configBase,
        defaultDate: dataInicioDefault,
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            this.filtros.dataHoraInicio = this.formatDateToISO(selectedDates[0]);
          }
        },
      });
      if (!this.filtros.dataHoraInicio) {
        this.filtros.dataHoraInicio = this.formatDateToISO(inicioHoje);
      }

      // Data/Hora Fim
      this.fpFim = flatpickr(this.$refs.datePickerFim, {
        ...configBase,
        defaultDate: dataFimDefault,
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            this.filtros.dataHoraFim = this.formatDateToISO(selectedDates[0]);
          }
        },
      });
      if (!this.filtros.dataHoraFim) {
        this.filtros.dataHoraFim = this.formatDateToISO(fimHoje);
      }
    },
    parseISOToDate(isoString) {
      // Converte "2026-02-14T10:30" para objeto Date
      const [datePart, timePart] = isoString.split('T');
      const [ano, mes, dia] = datePart.split('-').map(Number);
      const [hora, min] = timePart.split(':').map(Number);
      return new Date(ano, mes - 1, dia, hora, min);
    },
    formatDateToISO(date) {
      const ano = date.getFullYear();
      const mes = String(date.getMonth() + 1).padStart(2, '0');
      const dia = String(date.getDate()).padStart(2, '0');
      const hora = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${ano}-${mes}-${dia}T${hora}:${min}`;
    },
    limparFiltros() {
      this.verificarPerdaSelecao(() => {
        this.filtros.dataHoraInicio = "";
        this.filtros.dataHoraFim = "";
        this.filtros.statusFiltro = "ativas";
        this.offset = 0;
        if (this.fpInicio) this.fpInicio.clear();
        if (this.fpFim) this.fpFim.clear();
        // Limpa os query params da URL
        this.$router.replace({ query: {} }).catch(() => {});
        this.limparSelecao();
        this.buscarLeituras();
      });
    },
    aplicarFiltros() {
      this.verificarPerdaSelecao(() => {
        this.offset = 0;
        this.limparSelecao();
        this.buscarLeituras();
      });
    },
    async carregarDispositivo() {
      try {
        const response = await this.urlGet(`/AdminLeituras/Dispositivo/${this.qrCodeId}`);
        if (response.data) {
          this.dispositivo = response.data;
        }
      } catch (error) {
        console.error("Erro ao carregar dispositivo:", error);
      }
    },
    async buscarLeituras() {
      // Aguarda o próximo tick para garantir que os valores do v-model estão atualizados
      await this.$nextTick();

      this.loading = true;
      try {
        // Mapeia o statusFiltro para os parâmetros da API
        let includeDeleted = false;
        let onlyDeleted = false;

        if (this.filtros.statusFiltro === 'todas') {
          includeDeleted = true;
        } else if (this.filtros.statusFiltro === 'deletadas') {
          includeDeleted = true;
          onlyDeleted = true;
        }

        const payload = {
          qrCodeId: parseInt(this.qrCodeId),
          limit: this.limit,
          offset: this.offset,
          includeDeleted: includeDeleted,
          onlyDeleted: onlyDeleted,
          orderBy: "datetime",
          orderDirection: "DESC",
        };

        // Pega os valores diretamente do estado atual
        // O formato do datetime-local é YYYY-MM-DDTHH:mm
        // Enviamos diretamente sem conversão de timezone para manter o horário selecionado
        const dataInicio = this.filtros.dataHoraInicio;
        const dataFim = this.filtros.dataHoraFim;

        if (dataInicio) {
          // Adiciona segundos e envia como string ISO sem conversão de timezone
          payload.dataInicio = dataInicio + ":00";
        }
        if (dataFim) {
          payload.dataFim = dataFim + ":00";
        }

        console.log("Filtros enviados:", { dataInicio, dataFim, statusFiltro: this.filtros.statusFiltro, payload });

        const response = await this.urlPost("/AdminLeituras/Read", payload);
        if (response.data) {
          this.leituras = response.data.itens || [];
          this.total = response.data.total || 0;
        }

        // Atualiza a URL com os filtros atuais
        this.atualizarUrl();
      } catch (error) {
        console.error("Erro ao buscar leituras:", error);
        this.$refs.alert.show("Erro ao buscar leituras", "danger");
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return date.toLocaleString("pt-BR");
    },
    formatSensorType(tipo) {
      const tipos = {
        estacao: "Estação",
        chuva: "Chuva",
        folha: "Folha",
        solo: "Solo",
        energia: "Energia",
        energia_smtp: "Energia SMTP",
        sonda_hidrostatica: "Sonda Hidro",
        piranometro: "Piranômetro",
        sonda_laser: "Sonda Laser",
        sonda_nivel: "Sonda Nível",
        sonda_caixa: "Caixa d'Água",
        ambiente: "Ambiente",
        ritter: "Ritter",
        fotobio: "Fotobio",
        lut: "LUT",
      };
      return tipos[tipo] || tipo;
    },
    verDetalhes(leitura) {
      this.$router.push({
        name: "LeituraDetalhes",
        params: {
          qrCodeId: this.qrCodeId,
          metadataId: leitura.id,
        },
      });
    },
    confirmarDelete(leitura) {
      this.leituraSelecionada = leitura;
      this.deleteReason = "";
      $(this.$refs.modalDelete).modal({ backdrop: "static", keyboard: false });
    },
    confirmarRestore(leitura) {
      this.leituraSelecionada = leitura;
      $(this.$refs.modalRestore).modal({ backdrop: "static", keyboard: false });
    },
    fecharModalDelete() {
      $(this.$refs.modalDelete).modal("hide");
    },
    fecharModalRestore() {
      $(this.$refs.modalRestore).modal("hide");
    },
    async executarDelete() {
      if (!this.leituraSelecionada) return;

      this.deletando = true;
      try {
        await this.urlDelete(
          `/AdminLeituras/Metadata/${this.leituraSelecionada.id}`,
          { reason: this.deleteReason || null }
        );
        this.$refs.alert.show("Leitura deletada com sucesso", "success");
        $(this.$refs.modalDelete).modal("hide");
        this.buscarLeituras();
      } catch (error) {
        console.error("Erro ao deletar leitura:", error);
        this.$refs.alert.show("Erro ao deletar leitura", "danger");
      } finally {
        this.deletando = false;
      }
    },
    async executarRestore() {
      if (!this.leituraSelecionada) return;

      this.restaurando = true;
      try {
        await this.urlPost(`/AdminLeituras/Restore/Metadata/${this.leituraSelecionada.id}`);
        this.$refs.alert.show("Leitura restaurada com sucesso", "success");
        $(this.$refs.modalRestore).modal("hide");
        this.buscarLeituras();
      } catch (error) {
        console.error("Erro ao restaurar leitura:", error);
        this.$refs.alert.show("Erro ao restaurar leitura", "danger");
      } finally {
        this.restaurando = false;
      }
    },
    paginaAnterior() {
      if (this.offset >= this.limit) {
        this.verificarPerdaSelecao(() => {
          this.offset -= this.limit;
          this.limparSelecao();
          this.buscarLeituras();
        });
      }
    },
    proximaPagina() {
      if (this.offset + this.limit < this.total) {
        this.verificarPerdaSelecao(() => {
          this.offset += this.limit;
          this.limparSelecao();
          this.buscarLeituras();
        });
      }
    },
    alterarLimit(novoValor) {
      const novoLimit = parseInt(novoValor);
      if (!this.opcoesLimit.includes(novoLimit) || novoLimit === this.limit) return;
      this.verificarPerdaSelecao(() => {
        this.limit = novoLimit;
        this.offset = 0;
        this.limparSelecao();
        this.buscarLeituras();
      });
    },
    // ---------- Seleção em lote ----------
    toggleSelecionado(id) {
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
      }
      // Força reatividade do Set no Vue 2
      this.selectedIds = new Set(this.selectedIds);
    },
    toggleSelecionarTodos() {
      if (this.isTodosSelecionadosPagina) {
        this.leituras.forEach(l => this.selectedIds.delete(l.id));
      } else {
        this.leituras.forEach(l => this.selectedIds.add(l.id));
      }
      this.selectedIds = new Set(this.selectedIds);
    },
    limparSelecao() {
      this.selectedIds = new Set();
    },
    verificarPerdaSelecao(acao) {
      if (this.selectedIds.size === 0) {
        acao();
        return;
      }
      this.acaoPosPerdaSelecao = acao;
      $(this.$refs.modalPerderSelecao).modal({ backdrop: "static", keyboard: false });
    },
    confirmarPerdaSelecao() {
      const acao = this.acaoPosPerdaSelecao;
      this.acaoPosPerdaSelecao = null;
      $(this.$refs.modalPerderSelecao).modal("hide");
      if (typeof acao === 'function') acao();
    },
    fecharModalPerderSelecao() {
      this.acaoPosPerdaSelecao = null;
      $(this.$refs.modalPerderSelecao).modal("hide");
    },
    // ---------- Delete em lote ----------
    confirmarDeleteBulk() {
      if (this.qtdSelecionadasAtivas === 0) return;
      this.bulkDeleteReason = "";
      $(this.$refs.modalDeleteBulk).modal({ backdrop: "static", keyboard: false });
    },
    fecharModalDeleteBulk() {
      $(this.$refs.modalDeleteBulk).modal("hide");
    },
    async executarDeleteBulk() {
      const ids = this.leituras
        .filter(l => this.selectedIds.has(l.id) && !l.isDeleted)
        .map(l => l.id);

      if (ids.length === 0) return;

      this.deletandoBulk = true;
      try {
        const response = await this.urlPost("/AdminLeituras/BulkDelete", {
          metadataIds: ids,
          reason: this.bulkDeleteReason || null,
        });
        const qtd = response.data || ids.length;
        this.$refs.alert.show(
          `${qtd} ${qtd === 1 ? 'leitura deletada' : 'leituras deletadas'} com sucesso`,
          "success"
        );
        $(this.$refs.modalDeleteBulk).modal("hide");
        this.limparSelecao();
        this.buscarLeituras();
      } catch (error) {
        console.error("Erro ao deletar leituras em lote:", error);
        this.$refs.alert.show("Erro ao deletar leituras em lote", "danger");
      } finally {
        this.deletandoBulk = false;
      }
    },
    // ---------- Restore em lote ----------
    confirmarRestoreBulk() {
      if (this.qtdSelecionadasDeletadas === 0) return;
      $(this.$refs.modalRestoreBulk).modal({ backdrop: "static", keyboard: false });
    },
    fecharModalRestoreBulk() {
      $(this.$refs.modalRestoreBulk).modal("hide");
    },
    async executarRestoreBulk() {
      const ids = this.leituras
        .filter(l => this.selectedIds.has(l.id) && l.isDeleted)
        .map(l => l.id);

      if (ids.length === 0) return;

      this.restaurandoBulk = true;
      try {
        const response = await this.urlPost("/AdminLeituras/BulkRestore", {
          metadataIds: ids,
        });
        const qtd = response.data || ids.length;
        this.$refs.alert.show(
          `${qtd} ${qtd === 1 ? 'leitura restaurada' : 'leituras restauradas'} com sucesso`,
          "success"
        );
        $(this.$refs.modalRestoreBulk).modal("hide");
        this.limparSelecao();
        this.buscarLeituras();
      } catch (error) {
        console.error("Erro ao restaurar leituras em lote:", error);
        this.$refs.alert.show("Erro ao restaurar leituras em lote", "danger");
      } finally {
        this.restaurandoBulk = false;
      }
    },
    voltar() {
      this.$router.push({ name: "Dispositivos" });
    },
    formatLocalizacao() {
      if (!this.dispositivo) return '-';
      const partes = [];
      if (this.dispositivo.municipio) partes.push(this.dispositivo.municipio);
      if (this.dispositivo.uf) partes.push(this.dispositivo.uf);
      return partes.length > 0 ? partes.join(' - ') : '-';
    },
  },
};
</script>

<style scoped>
.table-danger {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.table-active {
  background-color: rgba(0, 96, 53, 0.08) !important;
}

.bulk-action-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #ffffff;
}

.bulk-action-inner {
  background-color: #eaf5ef;
  border: 1px solid rgba(0, 96, 53, 0.35);
  color: #006035;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.bulk-action-bar i.bx-check-circle {
  font-size: 1.25rem;
  color: #006035;
}

/* Selecionar todos (mobile) */
.mobile-select-all {
  background-color: #f7f9fa;
  border: 1px solid #e3e7ea;
}

.mobile-select-all label {
  font-size: 0.9rem;
  color: #495057;
}

/* Toolbar de ação em lote — mobile (fixa no rodapé) */
.bulk-toolbar-mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border-top: 1px solid rgba(0, 96, 53, 0.25);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
  z-index: 1030;
  color: #006035;
}

.bulk-toolbar-mobile i.bx-check-circle {
  font-size: 1.15rem;
}

.bulk-toolbar-mobile .btn-link {
  color: #006035;
  font-weight: 500;
}

/* Garante espaço para os cards não ficarem cobertos pela toolbar fixa */
.pb-bulk-toolbar {
  padding-bottom: 8rem;
}

/* Cards Mobile */
.leitura-card {
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.2s ease;
}

.leitura-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
}

.leitura-card.border-danger {
  background-color: rgba(220, 53, 69, 0.05);
}

.leitura-card .card-body {
  padding: 1rem;
}

/* Responsividade mobile */
@media (max-width: 767.98px) {
  .container-fluid {
    padding: 1rem !important;
  }

  .btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }

  .leitura-card .btn {
    padding: 0.625rem 0.75rem;
  }
}
</style>

<style>
/* Flatpickr customização com cor verde do projeto */
.flatpickr-calendar {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange,
.flatpickr-day.selected.inRange,
.flatpickr-day.startRange.inRange,
.flatpickr-day.endRange.inRange,
.flatpickr-day.selected:focus,
.flatpickr-day.startRange:focus,
.flatpickr-day.endRange:focus,
.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover,
.flatpickr-day.selected.prevMonthDay,
.flatpickr-day.startRange.prevMonthDay,
.flatpickr-day.endRange.prevMonthDay,
.flatpickr-day.selected.nextMonthDay,
.flatpickr-day.startRange.nextMonthDay,
.flatpickr-day.endRange.nextMonthDay {
  background: #006035 !important;
  border-color: #006035 !important;
  color: #fff !important;
}

.flatpickr-day.inRange,
.flatpickr-day.prevMonthDay.inRange,
.flatpickr-day.nextMonthDay.inRange,
.flatpickr-day.today.inRange,
.flatpickr-day.prevMonthDay.today.inRange,
.flatpickr-day.nextMonthDay.today.inRange,
.flatpickr-day:hover,
.flatpickr-day.prevMonthDay:hover,
.flatpickr-day.nextMonthDay:hover,
.flatpickr-day:focus,
.flatpickr-day.prevMonthDay:focus,
.flatpickr-day.nextMonthDay:focus {
  background: rgba(0, 96, 53, 0.1) !important;
  border-color: rgba(0, 96, 53, 0.2) !important;
}

.flatpickr-day.today {
  border-color: #006035 !important;
}

.flatpickr-day.today:hover,
.flatpickr-day.today:focus {
  background: #006035 !important;
  border-color: #006035 !important;
  color: #fff !important;
}

.flatpickr-months .flatpickr-prev-month:hover svg,
.flatpickr-months .flatpickr-next-month:hover svg {
  fill: #006035 !important;
}

.flatpickr-current-month .flatpickr-monthDropdown-months:hover,
.flatpickr-current-month input.cur-year:hover {
  background: rgba(0, 96, 53, 0.1);
}

/* Time picker */
.flatpickr-time input:hover,
.flatpickr-time .flatpickr-am-pm:hover,
.flatpickr-time input:focus,
.flatpickr-time .flatpickr-am-pm:focus {
  background: rgba(0, 96, 53, 0.1) !important;
}

.flatpickr-time .numInputWrapper span.arrowUp:after {
  border-bottom-color: #006035 !important;
}

.flatpickr-time .numInputWrapper span.arrowDown:after {
  border-top-color: #006035 !important;
}

.flatpickr-time .numInputWrapper:hover {
  background: rgba(0, 96, 53, 0.05);
}

/* Input focus */
input.flatpickr-input:focus {
  border-color: #006035;
  box-shadow: 0 0 0 0.2rem rgba(0, 96, 53, 0.25);
}
</style>
